// Service portal cidadao do modulo Recurso de Transito
import { supabase } from '../lib/supabase';

export type TipoPedidoRT = 'defesa_previa' | 'recurso_multa' | 'indicacao_condutor';

export const TIPO_PEDIDO_RT_LABEL: Record<TipoPedidoRT, string> = {
  defesa_previa: 'Defesa prévia',
  recurso_multa: 'Recurso de multa (JARI)',
  indicacao_condutor: 'Indicação de condutor',
};

export const TIPO_PEDIDO_RT_DESCRICAO: Record<TipoPedidoRT, string> = {
  defesa_previa: 'Apresente sua defesa antes da notificação de penalidade. A defesa visa anular o auto de infração antes da multa ser aplicada.',
  recurso_multa: 'Recorra da multa já aplicada. A JARI (Junta Administrativa de Recursos de Infrações) analisará e poderá anular ou manter a penalidade. Em caso de indeferimento, você ainda pode recorrer ao CETRAN.',
  indicacao_condutor: 'Identifique o real condutor quando o proprietário do veículo não estava ao volante no momento da infração.',
};

export interface AssuntoRT {
  id: string;
  nome: string;
  descricao: string | null;
  prazo_dias: number | null;
}

export interface RecursoArvoreRT {
  id: string;
  numero: string;
  titulo: string;
  status: string;
  data_abertura: string;
  data_conclusao: string | null;
  documento_pai_id: string | null;
  instancia_recurso: number | null;
  instancia_label: 'JARI' | 'CETRAN' | null;
  nivel: number;
}

export interface IndicadoresRT {
  ano: number;
  total_pedidos: number;
  deferidos: number;
  indeferidos: number;
  indeferidos_parciais: number;
  respondidos_no_prazo: number;
  respondidos_fora_do_prazo: number;
  tempo_medio_resposta_horas: number;
  recursos: { jari: number; cetran: number };
  distribuicao_por_tipo: Record<string, number>;
}

export const recursoTransitoPortalService = {
  async listarAssuntos(estabelecimentoId: string): Promise<AssuntoRT[]> {
    const { data, error } = await supabase
      .from('assuntos')
      .select('id, nome, descricao, prazo_dias')
      .eq('estabelecimento_id', estabelecimentoId)
      .eq('modulo_codigo', 'requerimento_transito')
      .eq('publica', true)
      .eq('ativo', true)
      .is('deletado_em', null)
      .order('nome');
    if (error) throw error;
    return (data ?? []) as AssuntoRT[];
  },

  async abrir(args: {
    estabelecimento_id: string;
    cidadao_id: string;
    assunto_id: string;
    tipo_pedido: TipoPedidoRT;
    titulo: string;
    descricao: string;
    numero_ait?: string;
    placa_veiculo?: string;
    data_infracao?: string;
    local_infracao?: string;
    codigo_infracao?: string;
    valor_multa?: number;
    condutor_nome?: string;
    condutor_cpf?: string;
  }): Promise<{ id: string; numero: string; hash_publico: string }> {
    const { data, error } = await supabase
      .from('documentos')
      .insert({
        estabelecimento_id: args.estabelecimento_id,
        modulo_codigo: 'requerimento_transito',
        assunto_id: args.assunto_id,
        titulo: args.titulo,
        descricao: args.descricao,
        status: 'aberto',
        prioridade: 'media',
        visibilidade: 'interna',
        solicitante_cidadao_id: args.cidadao_id,
        campos_customizados: {
          tipo_pedido_rt: args.tipo_pedido,
          ...(args.numero_ait ? { numero_ait: args.numero_ait } : {}),
          ...(args.placa_veiculo ? { placa_veiculo: args.placa_veiculo.toUpperCase() } : {}),
          ...(args.data_infracao ? { data_infracao: args.data_infracao } : {}),
          ...(args.local_infracao ? { local_infracao: args.local_infracao } : {}),
          ...(args.codigo_infracao ? { codigo_infracao: args.codigo_infracao } : {}),
          ...(args.valor_multa ? { valor_multa: args.valor_multa } : {}),
          ...(args.condutor_nome ? { condutor_nome: args.condutor_nome } : {}),
          ...(args.condutor_cpf ? { condutor_cpf: args.condutor_cpf } : {}),
        },
      })
      .select('id, numero, hash_publico')
      .single();
    if (error) throw error;
    return data as { id: string; numero: string; hash_publico: string };
  },

  async recorrerCetran(documentoPaiId: string, justificativa: string): Promise<{ documento_id: string; numero: string; instancia: number }> {
    const { data, error } = await supabase.rpc('recurso_transito_recorrer_cetran', {
      p_documento_pai_id: documentoPaiId,
      p_justificativa: justificativa,
    });
    if (error) throw error;
    if (data && typeof data === 'object' && 'erro' in data) {
      throw new Error(String((data as { erro: string }).erro));
    }
    return data as { documento_id: string; numero: string; instancia: number };
  },

  async listarRecursos(documentoId: string): Promise<RecursoArvoreRT[]> {
    const { data, error } = await supabase.rpc('recurso_transito_listar_recursos', { p_documento_id: documentoId });
    if (error) throw error;
    return (data ?? []) as RecursoArvoreRT[];
  },

  async indicadores(estabelecimentoId: string, ano?: number): Promise<IndicadoresRT> {
    const { data, error } = await supabase.rpc('recurso_transito_indicadores_publico', {
      p_estabelecimento_id: estabelecimentoId,
      p_ano: ano ?? null,
    });
    if (error) throw error;
    return data as IndicadoresRT;
  },
};
